import { z } from 'zod';
import {
  AvailabilityDaySchema,
  AvailabilityEntrySchema,
  AvailabilityExceptionSchema,
  AvailabilityRangeResponseSchema,
  AvailabilityRuleSchema,
  AvailabilityStatusSchema,
  BatchReplaceDaysResponseSchema,
  BulkAvailabilityResponseSchema,
  CalendarContractSchema,
  DayMutationResponseSchema,
  DeleteDayResponseSchema,
  DeleteRuleResponseSchema,
  DetailModeSchema,
  ProviderServiceListingSummarySchema,
  ShiftLabelSchema,
  ShiftTypeSchema,
  WorkLocationSchema,
  WritableAvailabilityEntrySchema,
  WritableAvailabilityExceptionSchema,
  WritableAvailabilityRulePatchSchema,
  WritableAvailabilityRuleSchema,
} from './schemas';

export type AvailabilityEntry = z.infer<typeof AvailabilityEntrySchema>;
export type AvailabilityDay = z.infer<typeof AvailabilityDaySchema>;
export type CalendarContract = z.infer<typeof CalendarContractSchema>;
export type AvailabilityRangeResponse = z.infer<typeof AvailabilityRangeResponseSchema>;
export type BulkAvailabilityResponse = z.infer<typeof BulkAvailabilityResponseSchema>;
export type DayMutationResponse = z.infer<typeof DayMutationResponseSchema>;
export type BatchReplaceDaysResponse = z.infer<typeof BatchReplaceDaysResponseSchema>;
export type DeleteDayResponse = z.infer<typeof DeleteDayResponseSchema>;
export type DeleteRuleResponse = z.infer<typeof DeleteRuleResponseSchema>;
export type AvailabilityRule = z.infer<typeof AvailabilityRuleSchema>;
export type AvailabilityException = z.infer<typeof AvailabilityExceptionSchema>;
export type ProviderServiceListingSummary = z.infer<typeof ProviderServiceListingSummarySchema>;
export type WritableAvailabilityEntry = z.infer<typeof WritableAvailabilityEntrySchema>;
export type WritableAvailabilityException = z.infer<typeof WritableAvailabilityExceptionSchema>;
export type WritableAvailabilityRule = z.infer<typeof WritableAvailabilityRuleSchema>;
export type WritableAvailabilityRulePatch = z.infer<typeof WritableAvailabilityRulePatchSchema>;
export type DetailMode = z.infer<typeof DetailModeSchema>;
export type ShiftLabel = z.infer<typeof ShiftLabelSchema>;
export type ShiftType = z.infer<typeof ShiftTypeSchema>;
export type WorkLocation = z.infer<typeof WorkLocationSchema>;
export type AvailabilityStatus = z.infer<typeof AvailabilityStatusSchema>;

export type AvailabilityScope =
  | { kind: 'all' }
  | { kind: 'general' }
  | { kind: 'listing'; listingId: number };

/** The provider's primary route is immediately editable; `all` remains an explicit overview. */
export const availabilityScopeFromParam = (value: string | null): AvailabilityScope => {
  if (value === 'all') return { kind: 'all' };
  if (!value || value === 'general') return { kind: 'general' };
  const listingId = Number(value);
  return Number.isSafeInteger(listingId) && listingId > 0
    ? { kind: 'listing', listingId }
    : { kind: 'general' };
};

export const availabilityScopeToParam = (scope: AvailabilityScope): string | null => {
  if (scope.kind === 'general') return null;
  if (scope.kind === 'all') return 'all';
  return String(scope.listingId);
};

export const availabilityScopeKey = (scope: AvailabilityScope): string =>
  scope.kind === 'listing' ? `listing:${scope.listingId}` : scope.kind;

export const scopeServiceListingId = (scope: AvailabilityScope): number | null | undefined => {
  if (scope.kind === 'all') return undefined;
  if (scope.kind === 'general') return null;
  return scope.listingId;
};

/** One date's replacement entries in a batch-replace ("template") request. */
export interface WritableBatchDay {
  date: string;
  entries: WritableAvailabilityEntry[];
}

export interface AvailabilityConflict {
  bookedContractIds: number[];
  entryIds: number[];
  heldByNegotiationIds: number[];
  message: string;
}
